import { useMobileLayout } from "../../hooks";
import {
	OptionRelatedInstituteSettings,
	ProctoringOption,
	ProctoringOptionProfileV2,
	ProctoringPlatforms,
} from "@verity/proctoring-option/types";
import { VerityInstituteSettings } from "../../types/common";
import { ReactNode, useEffect, useId, useRef, useState } from "react";
import { PROCTORING_OPTION_TEXT, ProctoringOptionCategory } from "../proctoring-options/option-text";
import {
	DEFAULT_OPTION_RELATED_INSTITUTE_SETTINGS,
	getSupportedOptionValue,
	isOptionSupportedForPlatform,
} from "@verity/proctoring-option";
import i18nPackage from "../../i18n/i18n";
import { ConditionalWrapper } from "../../wrapper";
import {
	CoreButton,
	CoreCheckbox,
	CoreIconButton,
	CoreInfoIndicator,
	CorePaper,
	CoreSpinner,
	coreToast,
} from "../../core";
import { Link, Typography } from "@mui/material";
import { isValidPlatform } from "../proctoring-options/proctoringOptionsGrid";
import LockdownMode from "../images/lockdownMode.svg?react";
import ExtensionMode from "../images/browserMode.svg?react";
import EnforceProfileIcon from "../images/forceSelectPorfile.svg?react";
import CrossSvg from "../images/grey-x.svg?react";
import GreenCheckMark from "../images/greenCheck.svg?react";
import styles from "./platform-step.module.css";
import { Trans } from "react-i18next";
import SelectProfileModal from "../edit-quiz-modals/selectProfileModal";
import { CoreButtonProps } from "../../core/button/Button";
import UnsupportedModal from "../edit-quiz-modals/unsupportedModal";

type PlatformStepProps<T extends Partial<ProctoringOption>> = SavedProfilesProps & ModeOptionsProps<T>;

export default function PlatformStep<T extends Partial<ProctoringOption>>({
	activeProfileId,
	enforcedProfilesOnly,
	hasInstituteProfilePermissions,
	loadProfile,
	clearProfile,
	option,
	profiles,
	refreshProfiles,
	setActiveProfileId,
	updateOption,
	verityInstituteSettings,
	disabled,
	lmsCourseId,
	loading,
	forceChromeOsManagedGuestSession = false,
	optionRelatedInstituteSettings,
	setHasActiveEnforcedProfile,
}: PlatformStepProps<T>) {
	const isMobileLayout = useMobileLayout();
	return (
		<div className={`${styles.platformSection} ${isMobileLayout ? styles.platformSectionCompact : ""}`}>
			<ModeOptions
				option={option}
				updateOption={updateOption}
				verityInstituteSettings={verityInstituteSettings}
				forceChromeOsManagedGuestSession={forceChromeOsManagedGuestSession}
				optionRelatedInstituteSettings={optionRelatedInstituteSettings}
				disabled={disabled}
			/>
			<SavedProfiles
				profiles={profiles}
				refreshProfiles={refreshProfiles}
				activeProfileId={activeProfileId}
				setActiveProfileId={setActiveProfileId}
				loadProfile={loadProfile}
				clearProfile={clearProfile}
				optionRelatedInstituteSettings={optionRelatedInstituteSettings}
				verityInstituteSettings={verityInstituteSettings}
				lmsCourseId={lmsCourseId}
				hasInstituteProfilePermissions={hasInstituteProfilePermissions}
				enforcedProfilesOnly={enforcedProfilesOnly}
				loading={loading}
				setHasActiveEnforcedProfile={setHasActiveEnforcedProfile}
			/>
		</div>
	);
}

export type SavedProfilesProps = {
	profiles: Array<ProctoringOptionProfileV2>;
	refreshProfiles: () => Promise<void | Map<number, ProctoringOptionProfileV2>>;
	activeProfileId: number | undefined;
	setActiveProfileId: (profileId: number | undefined) => void;
	loadProfile: (profile: ProctoringOptionProfileV2) => void;
	clearProfile: (profile: ProctoringOptionProfileV2) => void;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
	verityInstituteSettings: VerityInstituteSettings;
	lmsCourseId?: string;
	hasInstituteProfilePermissions: boolean;
	enforcedProfilesOnly: boolean;
	loading?: boolean;
	setHasActiveEnforcedProfile?: (hasActiveProfile: boolean) => void;
};

function SavedProfiles({
	profiles,
	refreshProfiles,
	activeProfileId,
	setActiveProfileId,
	loadProfile,
	clearProfile,
	optionRelatedInstituteSettings,
	verityInstituteSettings,
	lmsCourseId,
	hasInstituteProfilePermissions,
	enforcedProfilesOnly,
	loading,
	setHasActiveEnforcedProfile,
}: SavedProfilesProps) {
	const [selectProfileModalShowing, setSelectProfileModalShowing] = useState(false);
	const [filteredProfiles, setFilteredProfiles] = useState<Array<ProctoringOptionProfileV2>>([]);
	const [activeProfile, setActiveProfile] = useState<ProctoringOptionProfileV2>();
	const isMobileLayout = useMobileLayout();

	useEffect(() => {
		setFilteredProfiles(
			profiles.filter(
				(profile) =>
					(profile.proctoringOption.isExtension && verityInstituteSettings.allowExtensionPlatform) ||
					(profile.proctoringOption.isLockdown && verityInstituteSettings.allowLockdownPlatform) ||
					(profile.proctoringOption.isMobile && verityInstituteSettings.allowMobilePlatform),
			),
		);
	}, [profiles, verityInstituteSettings]);

	useEffect(() => {
		if (enforcedProfilesOnly && !activeProfile) {
			setHasActiveEnforcedProfile?.(false);
		} else if (enforcedProfilesOnly && activeProfile) {
			setHasActiveEnforcedProfile?.(true);
		}
	}, [enforcedProfilesOnly, setHasActiveEnforcedProfile, activeProfile]);

	useEffect(() => {
		setActiveProfile(activeProfileId ? filteredProfiles.find((profile) => profile.id === activeProfileId) : undefined);
	}, [activeProfileId, filteredProfiles]);

	const getActiveProfileName = () => {
		return activeProfile
			? (activeProfile.name ?? "-")
			: i18nPackage.t("editQuizPage.platformStep.savedProfiles.noProfile");
	};

	const content = activeProfile ? (
		<div className={styles.activeProfileContainer}>
			<GreenCheckMark className={styles.greenCheckMarkIcon} />
			<Typography component="span" className={styles.profileName}>
				{i18nPackage.t("editQuizPage.platformStep.savedProfiles.profileSelected")}
				{getActiveProfileName()}
			</Typography>

			<CoreIconButton
				onClick={() => {
					clearProfile(activeProfile);
					setActiveProfile(undefined);
				}}
				className={styles.removeIconButton}
				aria-label={i18nPackage.t("editQuizPage.platformStep.savedProfiles.profileSelected")}
				variant="plain"
			>
				<CrossSvg />
			</CoreIconButton>
		</div>
	) : enforcedProfilesOnly ? (
		<div className={`${styles.enforceProfileButtonContainer} ${isMobileLayout ? styles.compact : ""}}`}>
			<CoreButton onClick={() => setSelectProfileModalShowing(true)} disabled={loading}>
				{i18nPackage.t("editQuizPage.platformStep.savedProfiles.selectProfile")}
			</CoreButton>
		</div>
	) : (
		<div className={styles.selectProfileContainer}>
			<Typography component="span">
				<Trans
					i18nKey="editQuizPage.platformStep.savedProfiles.selectProfileLink"
					components={{
						a: (
							<Link
								type="button"
								component="button"
								aria-haspopup="dialog"
								onClick={() => setSelectProfileModalShowing(true)}
							/>
						),
					}}
					i18n={i18nPackage}
				/>
			</Typography>
		</div>
	);

	return (
		<>
			<ConditionalWrapper condition={!isMobileLayout} whenTrue={(children) => <div>{children}</div>}>
				<div className={styles.savedProfileRoot}>
					{loading && !isMobileLayout && (
						<div className={styles.loadingContainer}>
							<div className={styles.spinnerWrapper}>
								<CoreSpinner size={18} />
							</div>
						</div>
					)}
					{enforcedProfilesOnly && (
						<div className={styles.enforceProfileContainer}>
							<div className={styles.enforceTextContainer}>
								<Typography>
									{i18nPackage.t("editQuizPage.platformStep.savedProfiles.institutionRequiredProfiles")}
								</Typography>
							</div>
							<EnforceProfileIcon />
						</div>
					)}
					{!isMobileLayout && content}
				</div>
				{isMobileLayout && <div className={styles.stickyBottomBar}>{content}</div>}
				{selectProfileModalShowing && (
					<SelectProfileModal
						open={selectProfileModalShowing}
						profileList={filteredProfiles}
						refreshProfiles={refreshProfiles}
						activeProfileId={activeProfileId}
						setActiveProfileId={setActiveProfileId}
						loadProfile={(p) => {
							loadProfile(p);
							coreToast.success(i18nPackage.t("editQuizPage.platformStep.savedProfiles.toastSuccess"));
						}}
						lmsCourseId={lmsCourseId}
						optionRelatedInstituteSettings={optionRelatedInstituteSettings}
						verityInstituteSettings={verityInstituteSettings}
						enforcedProfilesOnly={enforcedProfilesOnly}
						hasInstituteProfilePermissions={hasInstituteProfilePermissions}
						onClose={() => setSelectProfileModalShowing(false)}
					/>
				)}
			</ConditionalWrapper>
		</>
	);
}

type ModeOptionsProps<T extends Partial<ProctoringOption>> = {
	option: T;
	updateOption: (options: T) => void;
	verityInstituteSettings: VerityInstituteSettings;
	forceChromeOsManagedGuestSession?: boolean;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
	disabled?: boolean;
	loading?: boolean;
	isInstitutionProfile?: boolean;
};

export function ModeOptions<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	verityInstituteSettings,
	forceChromeOsManagedGuestSession = false,
	optionRelatedInstituteSettings = DEFAULT_OPTION_RELATED_INSTITUTE_SETTINGS,
	disabled,
	loading,
	isInstitutionProfile = false,
}: ModeOptionsProps<T>) {
	const [isExtensionEnabled, setIsExtensionEnabled] = useState<boolean>(
		Boolean(option.isExtension && verityInstituteSettings.allowExtensionPlatform),
	);
	const [isMobileEnabled, setIsMobileEnabled] = useState<boolean>(
		Boolean(option.isMobile && verityInstituteSettings.allowMobilePlatform),
	);
	const [isLockdownEnabled, setIsLockdownEnabled] = useState<boolean>(
		Boolean(option.isLockdown && verityInstituteSettings.allowLockdownPlatform),
	);
	const [isChromeOsEnabled, setIsChromeOsEnabled] = useState<boolean>(Boolean(!isLockdownEnabled || option.isChromeOs));
	const [newOptions, setNewOptions] = useState<T>();
	const [platformUnsupportedOptionNames, setPlatformUnsupportedOptionNames] = useState<{
		[P in keyof ProctoringPlatforms | "none"]?: {
			[P in ProctoringOptionCategory]?: Array<{
				name: string;
				option: keyof ProctoringOption;
			}>;
		};
	}>();
	const [unsupportedModalOpen, setUnsupportedModalOpen] = useState(false);
	const initialLockdownMode = useRef(Boolean(option.isLockdown && verityInstituteSettings.allowLockdownPlatform));
	const initialOneScreenEnforcement = useRef(option.oneScreenEnforcementMode);
	const isMobileLayout = useMobileLayout();

	useEffect(() => {
		const extensionChange = Boolean(option.isExtension && verityInstituteSettings.allowExtensionPlatform);
		const mobileChange = Boolean(option.isMobile && verityInstituteSettings.allowMobilePlatform);
		const lockdownChange = Boolean(option.isLockdown && verityInstituteSettings.allowLockdownPlatform);
		const chromeOsChange = Boolean(extensionChange || option.isChromeOs);
		if (isExtensionEnabled !== extensionChange) {
			setIsExtensionEnabled(extensionChange);
		}
		if (isMobileEnabled !== mobileChange) {
			setIsMobileEnabled(mobileChange);
		}
		if (isLockdownEnabled !== lockdownChange) {
			setIsLockdownEnabled(lockdownChange);
		}
		if (isChromeOsEnabled !== chromeOsChange) {
			setIsChromeOsEnabled(chromeOsChange);
		}
	}, [option, verityInstituteSettings]);

	useEffect(() => {
		const unsupportedOptionNames: typeof platformUnsupportedOptionNames = {};
		const newOption = { ...option };

		newOption.isExtension = isExtensionEnabled;
		newOption.isLockdown = isLockdownEnabled;
		newOption.isMobile = isMobileEnabled;
		newOption.isChromeOs = isChromeOsEnabled;

		if (!initialLockdownMode.current) {
			if (isLockdownEnabled && !isInstitutionProfile) {
				newOption.oneScreenEnforcementMode = "normal";
			} else {
				newOption.oneScreenEnforcementMode = initialOneScreenEnforcement.current;
			}
		}

		// do not show warning modal if platform options did not change
		const showModal =
			newOption.isExtension !== Boolean(option.isExtension && verityInstituteSettings.allowExtensionPlatform) ||
			newOption.isMobile !== Boolean(option.isMobile && verityInstituteSettings.allowMobilePlatform) ||
			newOption.isLockdown !== Boolean(option.isLockdown && verityInstituteSettings.allowLockdownPlatform);

		const activePlatformKeys: (keyof ProctoringPlatforms)[] = [];
		if (isExtensionEnabled) activePlatformKeys.push("isExtension");
		if (isLockdownEnabled) activePlatformKeys.push("isLockdown");
		if (isMobileEnabled) activePlatformKeys.push("isMobile");

		Object.keys(newOption).forEach((k) => {
			const key = k as keyof ProctoringOption;
			const value = newOption[key];
			if (value !== undefined) {
				const supportedValue = getSupportedOptionValue(newOption, key, value);

				// Check if the other selected platforms support this option
				const isSupportedByAlternative = activePlatformKeys.some((p) => isOptionSupportedForPlatform(p, key, value));

				if (value !== supportedValue.value && !isSupportedByAlternative) {
					// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
					(newOption[key] as typeof supportedValue.value) = supportedValue.value;

					if (showModal) {
						const optionHeader = PROCTORING_OPTION_TEXT[key]?.header;
						const optionCategory = PROCTORING_OPTION_TEXT[key]?.category;
						if (optionHeader && optionCategory) {
							const text = `${optionHeader} ${getOptionModifierText(supportedValue.value, option[key])}`;
							const unsupportedOptionNamesEntry = {
								name: text,
								option: key,
							};
							const platform = supportedValue.platform ?? "none";
							if (!unsupportedOptionNames[platform]) {
								unsupportedOptionNames[platform] = { [optionCategory]: [] };
							} else if (!unsupportedOptionNames[platform]?.[optionCategory]) {
								unsupportedOptionNames[platform] = {
									...unsupportedOptionNames[platform],
									[optionCategory]: [],
								};
							}
							unsupportedOptionNames[platform]?.[optionCategory]?.push(unsupportedOptionNamesEntry);
						}
					}
				}
			}
		});

		if (showModal && Object.keys(unsupportedOptionNames).length > 0) {
			setPlatformUnsupportedOptionNames(unsupportedOptionNames);
			setUnsupportedModalOpen(true);
			setNewOptions(newOption);
		} else {
			updateOption(newOption);
		}
	}, [isExtensionEnabled, isMobileEnabled, isLockdownEnabled, isChromeOsEnabled]);

	const handleExtensionChange = () => {
		if (isExtensionEnabled && isLockdownEnabled) {
			setIsExtensionEnabled(false);
		} else {
			setIsExtensionEnabled((prev) => {
				if (!prev) {
					setIsLockdownEnabled(false);
					setIsChromeOsEnabled(true);
				}
				return true;
			});
		}
	};

	const handleLockdownChange = () => {
		if (isExtensionEnabled && isLockdownEnabled) {
			setIsLockdownEnabled(false);
		} else {
			setIsLockdownEnabled((prev) => {
				if (!prev) setIsExtensionEnabled(false);
				return true;
			});
		}
	};

	const onCloseUnsupportedModal = () => {
		setPlatformUnsupportedOptionNames(undefined);
		setNewOptions(undefined);
		setUnsupportedModalOpen(false);

		const newIsExtensionEnabled = Boolean(option.isExtension && verityInstituteSettings.allowExtensionPlatform);
		setIsExtensionEnabled(newIsExtensionEnabled);
		setIsLockdownEnabled(Boolean(option.isLockdown && verityInstituteSettings.allowLockdownPlatform));
		setIsMobileEnabled(Boolean(option.isMobile && verityInstituteSettings.allowMobilePlatform));
		setIsChromeOsEnabled(Boolean(newIsExtensionEnabled || option.isChromeOs));
	};
	// Declare separately to make linter happy
	const instructionLinkAriaLabel = i18nPackage.t("editQuizPage.platformStep.instruction.ariaLabel");

	return (
		<>
			{!disabled && (
				<ConditionalWrapper
					condition={isMobileLayout}
					whenTrue={(children) => <CorePaper className={styles.proctoringPlatformContainer}>{children}</CorePaper>}
					whenFalse={(children) => <div>{children}</div>}
				>
					<div className={styles.proctorSettingsGridPlatformsText}>
						<Trans
							i18nKey={"editQuizPage.platformStep.instruction.text"}
							i18n={i18nPackage}
							components={{
								a: (
									<Link
										href="https://support.yuja.com/hc/en-us/articles/24085929932567-YuJa-Verity-Test-Proctoring-Overview#01JFTX1PEYZH76PBSADCB21J6C"
										target="_blank"
										rel="noreferrer"
										aria-label={instructionLinkAriaLabel}
									/>
								),
							}}
						/>
					</div>
					{loading ? (
						<div className={styles.platformSpinnerContainer}>
							<CoreSpinner />
						</div>
					) : (
						<>
							{!isValidPlatform({ option, verityInstituteSettings }) && (
								<div className={styles.proctorSettingsGridPlatformsText} style={{ color: "red", marginTop: "13px" }}>
									{i18nPackage.t("editQuizPage.platformStep.description")}
								</div>
							)}
							<div
								className={styles.proctorSettingsGridPlatforms}
								style={{
									marginTop: isMobileLayout ? "0px" : "30px",
								}}
							>
								{verityInstituteSettings.allowExtensionPlatform && (
									<div
										className={styles.platformButtonWrapper}
										style={{
											height: isMobileLayout ? "fit-content" : "",
										}}
									>
										<EditQuizButton
											onClick={handleExtensionChange}
											checked={isExtensionEnabled}
											disabled={disabled}
											title={i18nPackage.t("editQuizPage.platformStep.browser.title")}
											description={
												isMobileLayout
													? i18nPackage.t("editQuizPage.platformStep.browser.descriptionShortened")
													: i18nPackage.t("editQuizPage.platformStep.browser.description")
											}
											icon={<ExtensionMode />}
										/>

										{verityInstituteSettings.allowMobilePlatform && isExtensionEnabled && (
											<MobileCheckBox
												disabled={disabled}
												isMobileEnabled={isMobileEnabled}
												setIsMobileEnabled={setIsMobileEnabled}
											/>
										)}
									</div>
								)}
								{verityInstituteSettings.allowLockdownPlatform && (
									<div
										className={styles.platformButtonWrapper}
										style={{
											height: isMobileLayout ? "fit-content" : "",
										}}
									>
										<EditQuizButton
											onClick={handleLockdownChange}
											checked={isLockdownEnabled}
											disabled={disabled}
											icon={<LockdownMode />}
											title={i18nPackage.t("editQuizPage.platformStep.lockdown.title")}
											description={
												isMobileLayout
													? i18nPackage.t("editQuizPage.platformStep.lockdown.descriptionShortened")
													: i18nPackage.t("editQuizPage.platformStep.lockdown.description")
											}
										/>

										<div className={styles.checkboxes}>
											{verityInstituteSettings.allowMobilePlatform && isLockdownEnabled && (
												<MobileCheckBox
													disabled={disabled}
													isMobileEnabled={isMobileEnabled}
													setIsMobileEnabled={setIsMobileEnabled}
												/>
											)}
											{isLockdownEnabled && !forceChromeOsManagedGuestSession && (
												<ChromeOsCheckbox
													disabled={disabled}
													checked={isChromeOsEnabled}
													onChange={setIsChromeOsEnabled}
													lockdownRequireManagedGuestSession={
														optionRelatedInstituteSettings.lockdownRequireManagedGuestSession
													}
												/>
											)}
										</div>
									</div>
								)}
							</div>
						</>
					)}
				</ConditionalWrapper>
			)}
			{unsupportedModalOpen && platformUnsupportedOptionNames && newOptions && (
				<UnsupportedModal
					open={unsupportedModalOpen}
					onClose={onCloseUnsupportedModal}
					onConfirm={() => {
						updateOption(newOptions);
						setPlatformUnsupportedOptionNames(undefined);
						setNewOptions(undefined);
						setUnsupportedModalOpen(false);
					}}
					platformUnsupportedOptionNames={platformUnsupportedOptionNames}
				/>
			)}
		</>
	);
}

function getOptionModifierText<T extends Partial<ProctoringOption>>(
	newValue: T[keyof T],
	oldValue: T[keyof T],
): string {
	if (newValue === "tab" && oldValue === "system") {
		return i18nPackage.t("editQuizPage.platformStep.system");
	}
	if ((typeof newValue === "string" && newValue !== "off" && oldValue === "off") || newValue === true) {
		return i18nPackage.t("editQuizPage.platformStep.disabled");
	}
	return "";
}

type EditQuizButtonProps = Omit<CoreButtonProps, "onClick" | "disabled"> & {
	onClick: () => void;
	checked: boolean;
	disabled?: boolean;
	icon: React.ReactNode;
	title: string;
	description: string;
};

// Not sure what to call these
export function EditQuizButton({
	checked,
	description,
	disabled,
	icon,
	onClick,
	title,
	...props
}: EditQuizButtonProps) {
	const isMobileLayout = useMobileLayout();
	const id = useId();
	return (
		<CoreButton
			onClick={onClick}
			checked={checked}
			id={id}
			aria-pressed={checked}
			disabled={disabled}
			sx={{ width: "100%", height: isMobileLayout ? "fit-content" : "110px", ...props.sx }}
			{...props}
		>
			<label
				className={`${styles.protoredMethodOption} ${isMobileLayout ? styles.protoredMethodOptionCompact : ""}`}
				style={disabled ? { cursor: "default", opacity: 0.7 } : {}}
				htmlFor={id}
			>
				<div className={styles.protoredMethodOptionInside}>
					{icon}
					<span className={styles.protoredMethodTitle}>{title}</span>
				</div>
				<span className={styles.protoredMethodDescription}>{description}</span>
			</label>
		</CoreButton>
	);
}

function LabeledCheckbox({
	checked,
	onChange,
	disabled,
	header,
	hint,
	children,
}: {
	checked: boolean;
	onChange: (value: boolean) => void;
	disabled?: boolean;
	header: ReactNode;
	hint?: ReactNode;
	children: ReactNode;
}) {
	const id = useId();
	return (
		<div className={styles.labeledCheckboxContainer}>
			<div className={styles.labeledCheckboxWrapper}>
				<CoreCheckbox
					size="16px"
					onClick={() => onChange(!checked)}
					checked={checked}
					id={id}
					disabled={disabled}
					style={{ padding: "3px" }}
				/>
			</div>

			<label
				className={styles.protoredMethodOption}
				style={disabled ? { cursor: "default", opacity: 0.7 } : {}}
				htmlFor={id}
			>
				<div>
					<span className={styles.protoredMethodTitleSub}>{header}</span>
					{hint && (
						<span className={styles.proctoredMethodInfoIcon}>
							<CoreInfoIndicator hint={hint} size={20} />
						</span>
					)}
				</div>
				<span className={styles.protoredMethodDescriptionSub}>{children}</span>
			</label>
		</div>
	);
}

function MobileCheckBox({
	setIsMobileEnabled,
	isMobileEnabled,
	disabled,
}: {
	isMobileEnabled: boolean;
	setIsMobileEnabled: (value: boolean) => void;
	disabled?: boolean;
}) {
	return (
		<LabeledCheckbox
			checked={isMobileEnabled}
			onChange={setIsMobileEnabled}
			disabled={disabled}
			header={i18nPackage.t("editQuizPage.platformStep.mobile.title")}
			hint={i18nPackage.t("editQuizPage.platformStep.mobile.hint")}
		>
			{i18nPackage.t("editQuizPage.platformStep.mobile.description")}
		</LabeledCheckbox>
	);
}

function ChromeOsCheckbox({
	lockdownRequireManagedGuestSession,
	checked,
	onChange,
	disabled,
}: {
	lockdownRequireManagedGuestSession: boolean;
	checked: boolean;
	onChange: (value: boolean) => void;
	disabled?: boolean;
}) {
	return (
		<LabeledCheckbox
			checked={checked}
			onChange={onChange}
			disabled={disabled}
			header={i18nPackage.t("editQuizPage.platformStep.chromeOs.title")}
			hint={
				lockdownRequireManagedGuestSession
					? i18nPackage.t("editQuizPage.platformStep.chromeOs.hintManagedGuestSession")
					: i18nPackage.t("editQuizPage.platformStep.chromeOs.hintUnrestricted")
			}
		>
			{i18nPackage.t("editQuizPage.platformStep.chromeOs.description")}
		</LabeledCheckbox>
	);
}
